/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import * as Cesium from "cesium";
import { useCallback } from "react";
import {
  setCesiumPopupPosition,
  setHoveredCesiumItem,
  setSelectedCesiumItem,
} from "~/redux/reducers/mapReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";
import { CesiumMapEntity, CesiumMapItem } from "../types/CesiumMapItems";
import { useCesiumGeopickingTool } from "./useCesiumGeopickingTool";
import { useCesiumMeasurementTool } from "./useCesiumMeasurementTool";
import { useCesiumPositionTooltip } from "./useCesiumPositionTooltip";

export type PopupTypes = {
  click: boolean;
  hover: boolean;
};

export type CesiumEventType = "click" | "hover";

export const CesiumPopupOriginalPosition = "popupOriginalPos";

const AllowedTypes: Record<CesiumMapItem["type"], PopupTypes> = {
  measurement: { click: true, hover: true },
  geopicking: { click: true, hover: true },
  cad: { click: true, hover: false },
  imagery: { click: false, hover: false },
  terrain: { click: false, hover: false },
};

type UseCesiumEventHandlerReturn = {
  cesiumEventHandler: CesiumEventHandler;
  hoveredEntity?: CesiumMapEntity;
};

type CesiumEventHandleArgs = {
  eventType: CesiumEventType;
  position: Cesium.Cartesian2;
  scene: Cesium.Scene;
  viewer: Cesium.Viewer;
  hoveredId: string | undefined;
  selectedId: string | undefined;
};
type CesiumEventHandler = (args: CesiumEventHandleArgs) => void;

export function useCesiumEventHandler(): UseCesiumEventHandlerReturn {
  const dispatch = useAppDispatch();
  const { cesiumMeasurementTool } = useCesiumMeasurementTool();
  const { cesiumPositionTooltip } = useCesiumPositionTooltip();
  const { cesiumGeopickingTool } = useCesiumGeopickingTool();
  const activeTool = useAppSelector((s) => s.map.activeCesiumTool);

  const cesiumEventHandler: CesiumEventHandler = useCallback(
    ({ position, viewer, scene, eventType, hoveredId, selectedId }) => {
      if (!position || !viewer || !scene || !eventType) return;

      cesiumMeasurementTool({
        ellipsoid: Cesium.Ellipsoid.WGS84,
        position,
        viewer,
        activeTool,
        eventType,
      });

      cesiumPositionTooltip({
        position,
        viewer,
      });

      cesiumGeopickingTool({
        position,
        viewer,
        activeTool,
        eventType,
      });

      if (
        activeTool === "measurement" ||
        activeTool === "simpleMeasurement" ||
        activeTool === "geopicking"
      )
        return;

      // TESTING
      // if (eventType === "click") {
      //   const ray = viewer.camera.getPickRay(position);
      //   const p = scene.globe.pick(ray, scene);
      //   if (p) {
      //     Cesium.sampleTerrainMostDetailed(scene.terrainProvider, [
      //       Cesium.Cartographic.fromCartesian(p),
      //     ]).then((c) => console.log(c[0].height));
      //   }
      // }

      // if (eventType === "click" && position) {
      //   const clickedLocation = viewer.scene.pickPosition(position);
      //   const carto = Cesium.Cartographic.fromCartesian(clickedLocation);
      //   const height = viewer.scene.sampleHeight(carto);
      //   const p = Cesium.Cartesian3.fromRadians(
      //     carto.longitude,
      //     carto.latitude,
      //     height,
      //   );
      // console.log(clickedLocation);
      // console.log(carto);
      // console.log(height);
      // console.log(p);
      // const z = Cesium.Cartesian3.distance(
      //   p,
      //   new Cesium.Cartesian3(p.x, p.y, 5525284.531),
      // );
      // const zRange = Cesium.Cartesian3.distance(
      //   new Cesium.Cartesian3(p.x, p.y, 5525284.531),
      //   new Cesium.Cartesian3(p.x, p.y, 5525306.514),
      // );
      // console.log(`Click position to Z-min: ${z}`);
      // console.log(`Z range: ${zRange}`);
      // const x = Cesium.Cartesian3.distance(
      //   p,
      //   new Cesium.Cartesian3(2918444.76, p.y, p.z),
      // );
      // const xRange = Cesium.Cartesian3.distance(
      //   new Cesium.Cartesian3(2918444.76, p.y, p.z),
      //   new Cesium.Cartesian3(2918504.56, p.y, p.z),
      // );
      // console.log(`Click position to X-min: ${x}`);
      // console.log(`X range: ${xRange}`);
      // const y = Cesium.Cartesian3.distance(
      //   p,
      //   new Cesium.Cartesian3(p.x, 1195167.25, p.z),
      // );
      // const yRange = Cesium.Cartesian3.distance(
      //   new Cesium.Cartesian3(p.x, 1195167.25, p.z),
      //   new Cesium.Cartesian3(p.x, 1195214.94, p.z),
      // );
      // console.log(`Click position to Y-min: ${y}`);
      // console.log(`Y range: ${yRange}`);
      //   if (p) {
      //     const marker = new Cesium.Entity({
      //       name: "location",
      //       position: p,
      //       point: {
      //         pixelSize: 5,
      //         color: Cesium.Color.RED,
      //         outlineColor: Cesium.Color.WHITE,
      //         outlineWidth: 2,
      //         // heightReference: HeightReference.NONE
      //       },
      //     });
      //     viewer.entities.add(marker);
      //     // console.log(marker);
      //   }
      // }

      // TESTING

      // Try to find an entity from the position
      const item = scene.pick(position);
      if (!item || !(item?.id instanceof Cesium.Entity)) {
        if (hoveredId) {
          dispatch(setHoveredCesiumItem(undefined));
        }
        if (selectedId && eventType === "click") {
          dispatch(setSelectedCesiumItem(undefined));
        }
        if (
          (hoveredId && !selectedId) ||
          (selectedId && eventType === "click")
        ) {
          dispatch(setCesiumPopupPosition(undefined));
        }
        document.body.style.cursor = "default";
        return;
      }
      if (isEntityItem(item.id)) {
        const now = Cesium.JulianDate.now();
        const entity = item.id as Cesium.Entity;
        if (!entity || !entity.properties) return;
        const props = entity.properties?.getValue(now) as CesiumMapItem;
        const { type, mapId } = props;
        if (
          (mapId === hoveredId && eventType === "hover") ||
          (mapId === selectedId && eventType === "click")
        ) {
          return;
        }

        // Check if item allows click or hover
        if (
          (entity.id && type in AllowedTypes && AllowedTypes[type].click) ||
          AllowedTypes[type].hover
        ) {
          document.body.style.cursor = "pointer";
          if (
            (eventType === "hover" && AllowedTypes[type].hover) ||
            (eventType === "click" && AllowedTypes[type].click)
          ) {
            let setPopupPosition = false;
            if (
              eventType === "hover" &&
              !selectedId &&
              hoveredId !== props.mapId
            ) {
              dispatch(setHoveredCesiumItem(props));
              setPopupPosition = true;
            } else if (eventType === "click") {
              dispatch(setSelectedCesiumItem(props));
              dispatch(setHoveredCesiumItem(undefined));
              setPopupPosition = true;
            }
            if (!setPopupPosition) return;
            const hasPos = !!entity.position;
            const pos = hasPos
              ? scene.cartesianToCanvasCoordinates(
                  entity.position!.getValue(now),
                )
              : position;

            // For handling the movement of the canvas
            if (!hasPos) {
              const carte3 = viewer.camera.pickEllipsoid(position);
              if (carte3) {
                if (entity.properties.hasProperty(CesiumPopupOriginalPosition))
                  entity.properties.removeProperty(CesiumPopupOriginalPosition);

                entity.properties.addProperty(CesiumPopupOriginalPosition, {
                  x: carte3.x,
                  y: carte3.y,
                  z: carte3.z,
                });
              }
            }
            // Billboard/marker is 48*48. Tooltip arrow is 10x10
            // x-axis values are divided by 2 to center the tooltip
            const markerSize = 48;
            const arrowSize = 10;
            dispatch(
              setCesiumPopupPosition({
                bottom: hasPos
                  ? viewer.canvas.clientHeight - pos.y + markerSize + arrowSize
                  : viewer.canvas.clientHeight - pos.y + arrowSize,
                left: pos.x - markerSize / 2 + arrowSize / 2,
              }),
            );
          }
        }
      }
    },
    [
      cesiumMeasurementTool,
      activeTool,
      cesiumPositionTooltip,
      cesiumGeopickingTool,
      dispatch,
    ],
  );

  return { cesiumEventHandler };
}

function isEntityItem(item: unknown): boolean {
  return item instanceof Cesium.Entity;
}
